import { EntryNode } from './entry-node';
import { CommonUtils } from '../../tool/common-utils';

export class KeyboardNavigator<T> {
  private rows: readonly EntryNode<T>[] = [];
  private activeIndex = -1;
  private _activeToken = 0;

  public setRows(rows: readonly EntryNode<T>[]): void {
    const previousActiveId = this.activeId;
    this.rows = rows;
    this.activeIndex =
      previousActiveId === undefined
        ? -1
        : rows.findIndex((row) => row.id === previousActiveId);
  }

  public get activeId(): unknown {
    return this.rows[this.activeIndex]?.id;
  }

  public get activeNode(): EntryNode<T> | undefined {
    return this.rows[this.activeIndex];
  }

  /**
   * Bumped on every explicit move (even when the resulting row is the same
   * as before, e.g. moveFirst() called twice in a row). Consumers that must
   * react to "focus this row now" as an action rather than a value change
   * (activeId can recompute to an equal value, which signals treat as a
   * no-op) should depend on this token instead of / in addition to activeId.
   */
  public get activeToken(): number {
    return this._activeToken;
  }

  public moveNext(): void {
    if (this.rows.length === 0) {
      return;
    }
    this.activeIndex =
      this.activeIndex === -1 ? 0 : (this.activeIndex + 1) % this.rows.length;
    this._activeToken++;
  }

  public movePrevious(): void {
    if (this.rows.length === 0) {
      return;
    }
    this.activeIndex =
      this.activeIndex === -1
        ? this.rows.length - 1
        : (this.activeIndex - 1 + this.rows.length) % this.rows.length;
    this._activeToken++;
  }

  public moveFirst(): void {
    if (this.rows.length === 0) {
      return;
    }
    this.activeIndex = 0;
    this._activeToken++;
  }

  public moveLast(): void {
    if (this.rows.length === 0) {
      return;
    }
    this.activeIndex = this.rows.length - 1;
    this._activeToken++;
  }

  public moveTo(id: unknown): void {
    const index = this.rows.findIndex((row) => row.id === id);
    if (index !== -1) {
      this.activeIndex = index;
      this._activeToken++;
    }
  }

  public typeahead(query: string): void {
    if (this.rows.length === 0 || query.length === 0) {
      return;
    }
    const normalizedQuery = CommonUtils.normalize(query, true);
    const startIndex = this.activeIndex === -1 ? 0 : this.activeIndex + 1;
    for (let offset = 0; offset < this.rows.length; offset++) {
      const index = (startIndex + offset) % this.rows.length;
      if (this.rows[index].searchIndex.primary.startsWith(normalizedQuery)) {
        this.activeIndex = index;
        this._activeToken++;
        return;
      }
    }
  }

  public activateCurrent(): EntryNode<T> | undefined {
    return this.activeNode;
  }
}
